// src/pages/admin/depositAutoMatch.js
//
// Suggested deposit ↔ bank matches for Daily Deposits, by amount AND by what
// the bank line says it is. Same idea as the Cash Flow reconciler's memo
// matching (backend apps/schedule_payment/memo_match.py): the amount says how
// much, the memo says what KIND of money it was, and the memo can refuse a pair
// the amount alone would have made.
//
// Suggestions only. Nothing here is saved — a person accepts a suggestion and
// it becomes a normal saved match (POST /api/deposit-matches/). Every rule
// pairs exact totals except `near`, whose difference is saved as a variance.
//
// MEASURED, NOT GUESSED: the vocabulary below comes from Sep 20 – Oct 20, 2026
// — 2,961 RBC deposit lines (NetSuite BankingTransaction.memo) across 30 clinic
// accounts and 276 Meridian lines (bank_daily_transaction.description).
import { classifyMemo } from "@utils/memoFormats";
import { addDaysISO } from "@utils/dateRanges";

// ─── bank-line categories ─────────────────────────────────────────────────────

export const VISA = "visa";
export const MASTERCARD = "mastercard";
export const AMEX = "amex";
export const DEBIT = "debit";
export const MONERIS = "moneris";
export const MONERIS_REVERSAL = "moneris_reversal";
export const INSURANCE = "insurance";
export const CHEQUE = "cheque";
export const ETRANSFER = "etransfer";
// Not patient money: internal transfers, loan draws, A/P payments, a card
// payment. ~40% of RBC deposit dollars in the sample. Never matched.
export const EXCLUDED = "excluded";

export const CATEGORY_LABELS = {
  [VISA]: "Visa",
  [MASTERCARD]: "Mastercard",
  [AMEX]: "Amex",
  [DEBIT]: "Debit",
  [MONERIS]: "Moneris",
  [MONERIS_REVERSAL]: "Moneris reversal",
  [INSURANCE]: "Insurance",
  [CHEQUE]: "Cheque",
  [ETRANSFER]: "e-Transfer",
  [EXCLUDED]: "Transfer / loan",
};

// Tested BEFORE classifyMemo. `MISC PAYMENT AMEX BANK OF CANADA PAYMENT` would
// otherwise read as an insurance-style misc payment, and `ACCOUNT TRANSFER` as
// nothing at all.
const EXCLUDE_PATTERNS = [
  /\bACCOUNT TRANSFER\b/i,
  /\bLOAN CR\b/i,
  /\bTRANSFER IN FROM\b/i,
  /\bACCOUNT PAYABLE PMT\b/i,
  /\bE-?TRANSFER CANCEL\b/i,
  /\bAMEX BANK OF CANADA PAYMENT\b/i,
  /^\s*WWW TRANSFER\b/i,
  /^\s*-\d+\s*$/, // bare "-1234567"
];

// classifyMemo is anchored at the start of the memo, which fits RBC
// ("VSA DEP13041934 ROYAL BANK …"). Meridian wraps the same codes:
// "Pre-Authorized VSA DEP12345 Other Reference # 9214" — strip the wrapper.
const MERIDIAN_PREFIX = /^\s*pre-?authorized\s+/i;
const MERIDIAN_SUFFIX = /\s+other reference\s*#.*$/i;

// Meridian names insurers without RBC's "INSURANCE" / "HEALTH/DENTAL CLAIM" /
// "MISC PAYMENT" lead word, so classifyMemo cannot see them. Whole words only.
const INSURER_PATTERN = new RegExp(
  "\\b(" +
    [
      "CDCP",
      "RCSD",
      "SUN ?LIFE",
      "SUNLIFE",
      "MANULIFE",
      "BLUE CROSS",
      "MEDAVIE",
      "CANADA LIFE",
      "GREEN SHIELD",
      "GSCI",
      "ESI CANADA",
      "EXPRESS SCRIPTS",
      "RWAM",
      "ACCERTA\\w*",
      "BENEVA",
      "EMPIRE",
      "GROUPHEALTH",
      "GROUPSOURCE",
      "NEXGENRX",
      "OEBAC",
      "CLAIMSECURE",
      "INSTREAM",
      "PEOPLE CORP",
      "COUGHLIN",
      "MANION WILKINS",
      "GLOBAL TRUST",
      "SCU DEN ASO",
      "MANITOBA FOOD",
      "RBC INSURANCE",
    ].join("|") +
    ")\\b",
  "i",
);

// classifyMemo kinds → our categories. `misc_payment` is the government /
// insurer ASO channel on this data (CDCP, Sun Life, Blue Cross, ESI).
const FROM_MEMO_KIND = {
  visa: VISA,
  mastercard: MASTERCARD,
  amex: AMEX,
  debit: DEBIT,
  moneris: MONERIS,
  insurance: INSURANCE,
  health_dental_claim: INSURANCE,
  misc_payment: INSURANCE,
  echeque: CHEQUE,
  etransfer: ETRANSFER,
};

/**
 * The category of one bank line from its memo / description, or null when the
 * text is not recognised (such a line can still pair, by exact amount only).
 */
export function classifyBankLine(text) {
  const raw = (text || "").trim();
  if (!raw) return null;
  if (EXCLUDE_PATTERNS.some((re) => re.test(raw))) return EXCLUDED;

  const core = raw.replace(MERIDIAN_PREFIX, "").replace(MERIDIAN_SUFFIX, "").trim();
  if (/^MON\s*REV/i.test(core)) return MONERIS_REVERSAL;

  const kind = classifyMemo(core);
  if (kind && FROM_MEMO_KIND[kind]) return FROM_MEMO_KIND[kind];

  if (/^COM REM CHQ DP\b/i.test(core) || /^DEPOSIT\b/i.test(core)) return CHEQUE;
  if (/^E-?TRANSFER IN\b/i.test(core)) return ETRANSFER;
  if (INSURER_PATTERN.test(core)) return INSURANCE;
  return null;
}

/** A card settlement category (Visa / MC / Amex / Debit / Moneris batch). */
export const isCardCategory = (category) =>
  [VISA, MASTERCARD, AMEX, DEBIT, MONERIS].includes(category);

// ─── deposit side ─────────────────────────────────────────────────────────────

// Collection Breakdown field → the bank category that pays it.
const CATEGORY_FOR_FIELD = {
  collection_visa: VISA,
  collection_mastercard: MASTERCARD,
  collection_amex: AMEX,
  collection_debit: DEBIT,
  collection_insurance_eft: INSURANCE,
  collection_checks: CHEQUE,
  collection_insurance_check: CHEQUE,
  collection_personal_eft: ETRANSFER,
};
const CARD_FIELDS = new Set([
  "collection_visa",
  "collection_mastercard",
  "collection_amex",
  "collection_debit",
]);

/** Deposit ids are "<YYYY-MM-DD>:<collection_field>". */
export const depositField = (deposit) => String(deposit.id).split(":")[1] || null;

// ─── verdict ──────────────────────────────────────────────────────────────────

export const BLOCKED = "blocked";
export const AGREE = "agree";
export const UNKNOWN = "unknown";

/**
 * What the bank text says about pairing one deposit line with one bank line.
 *   BLOCKED — the bank line is a different kind of money (a VSA DEP is not a
 *             debit deposit), or not patient money at all. Never paired.
 *   AGREE   — same kind. Exact amount + AGREE is the strongest suggestion.
 *   UNKNOWN — the bank text is not recognised; amount and date alone decide,
 *             and the suggestion is marked for checking.
 */
export function verdict(deposit, bankCategory, bankLine = null) {
  if (bankCategory === EXCLUDED || bankCategory === MONERIS_REVERSAL) return BLOCKED;
  if (bankCategory == null) return UNKNOWN;
  const field = depositField(deposit);
  // A debit batch names its own day: "EF1009 13396197" = Oct 9. Measured on
  // Sep 20 – Oct 20: the EF date equals the collection date in 182 of 184
  // suggested Debit pairs (RBC 165/167, Meridian 17/17); the 2 that disagreed
  // were same-amount coincidences. So a Debit line only pairs with ITS day.
  if (bankCategory === DEBIT && field === "collection_debit") {
    const efDay = efMonthDay(bankLine?.description);
    if (efDay && efDay !== String(deposit.date).slice(5)) return BLOCKED;
  }
  // A Moneris batch carries card money only; its pairing is the batch rule.
  if (bankCategory === MONERIS) return CARD_FIELDS.has(field) ? AGREE : BLOCKED;
  return CATEGORY_FOR_FIELD[field] === bankCategory ? AGREE : BLOCKED;
}

/** "MM-DD" from a debit memo's EF<MMDD> code, or null. */
export function efMonthDay(text) {
  const core = (text || "").replace(MERIDIAN_PREFIX, "");
  const m = /^\s*EF(\d{2})(\d{2})\b/i.exec(core);
  return m ? `${m[1]}-${m[2]}` : null;
}

// ─── matching ─────────────────────────────────────────────────────────────────

const cents = (n) => Math.round((Number(n) || 0) * 100);
const daysBetweenISO = (from, to) =>
  Math.round((new Date(`${to}T00:00:00`) - new Date(`${from}T00:00:00`)) / 86400000);

// A multi-line sum is "exact" only when it is short and close: ≤ 3 lines,
// on ≤ 2 distinct days, all within 3 days of the collection. Longer sums are
// suggested for checking ("check"), since chance totals get likely fast.
const SURE_SUM_LINES = 3;
const SURE_SUM_DAYS = 3;
// `near`: a 1:1 pair whose amounts differ by at most this many cents.
const NEAR_CENTS = 200;
const byDate = (a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0);

/**
 * Up to `maxItems` of `items` whose cents sum to `target`, or null. Depth-first
 * with pruning and a node budget, so a busy clinic-week cannot stall the page:
 * a match not found within budget is simply not suggested. The pruning assumes
 * positive amounts, so it is skipped when a negative card batch is in the pool.
 */
function subsetSum(items, target, { maxItems, budget = 20000 }) {
  const sorted = [...items].sort((a, b) => cents(b.amount) - cents(a.amount));
  const prune = sorted.every((b) => cents(b.amount) > 0);
  const suffix = [];
  let run = 0;
  for (let i = sorted.length - 1; i >= 0; i--) {
    run += cents(sorted[i].amount);
    suffix[i] = run;
  }
  let nodes = 0;
  const pick = [];
  const dfs = (start, remaining) => {
    if (remaining === 0) return pick.length > 0;
    if (pick.length >= maxItems || ++nodes > budget) return false;
    for (let i = start; i < sorted.length; i++) {
      const c = cents(sorted[i].amount);
      if (prune && c > remaining) continue;
      if (prune && suffix[i] < remaining) return false;
      pick.push(sorted[i]);
      if (dfs(i + 1, remaining - c)) return true;
      pick.pop();
    }
    return false;
  };
  return dfs(0, target) ? [...pick] : null;
}

/**
 * The 2+ of a day's card deposits (at most 4: Visa, MC, Amex, Debit) whose sum
 * is closest to `target`, within `tolerance` cents — or null. Small enough to
 * try every combination.
 */
function closestSubset(cards, target, tolerance) {
  let best = null;
  let bestOff = Infinity;
  for (let mask = 1; mask < 1 << cards.length; mask++) {
    const pick = cards.filter((_, i) => mask & (1 << i));
    if (pick.length < 2) continue;
    const off = Math.abs(pick.reduce((n, d) => n + cents(d.amount), 0) - target);
    if (off <= tolerance && off < bestOff) {
      best = pick;
      bestOff = off;
    }
  }
  return best;
}

/**
 * Suggest matches between one clinic's pending deposit lines and
 * pending bank lines. Rules run in order and each line is used at most once:
 *
 *   exact    1 deposit ↔ 1 bank line, equal to the cent, memo AGREES.
 *   sum      1 deposit ↔ SEVERAL bank lines of its type whose total equals it
 *            — any type: a day's Insurance EFT arrives as one payment per
 *            insurer (≤ 8 lines), a card day can settle as several VSA DEP
 *            lines, cheques as several eCheque deposits (≤ 6 lines). The bank
 *            lines may be on different days.
 *   moneris  one MON DEP line = a subset of one day's card deposits
 *            (Visa / MC / Amex / Debit), as Deposit Sheet already reads Moneris.
 *            Exact first; within NEAR_CENTS later, as "check".
 *   cheque   one cheque line = that day's Checks + Insurance cheque together.
 *   amount   1 ↔ 1, equal to the cent, memo UNKNOWN — confidence "check".
 *   near     1 ↔ 1, within NEAR_CENTS ($2) but not equal, memo AGREES —
 *            confidence "check"; the difference is the match's variance.
 *
 * Window: money is recorded by the clinic first and reaches the bank on the
 * same day or later, so every rule first runs on the collection date up to
 * `windowDays` after. Bank dates are sometimes wrong, so the rules then run
 * again on what is left with the window opened `backDays` BEFORE the
 * collection; a pair with the bank dated earlier is never "exact". Within a
 * window, the bank date closest to the collection wins.
 * Card deposits that arrive net of fees more than $2 short are NOT suggested.
 *
 * Returns { suggestions, deposits, bank, excluded }:
 *   suggestions: [{ deposits, banks, rule, confidence: "exact" | "check" }]
 *   deposits / bank: the lines left unmatched
 *   excluded: bank lines that are not patient money (transfers, loans, …)
 */
export function suggestMatches(depositLines, bankLines, { windowDays = 3, backDays = 3 } = {}) {
  const bank = [];
  const excluded = [];
  for (const b of bankLines) {
    const category = b.category ?? classifyBankLine(b.description);
    const line = { ...b, category };
    if (category === EXCLUDED) excluded.push(line);
    else bank.push(line);
  }

  const usedDep = new Set();
  const usedBank = new Set();
  const suggestions = [];
  const deposits = [...depositLines].sort(byDate);
  const freeBank = () => bank.filter((b) => !usedBank.has(b.id)).sort(byDate);
  // Days the window opens before the collection: 0 on the first pass.
  let back = 0;
  const dayInWindow = (day, b) =>
    b.date >= addDaysISO(day, -back) && b.date <= addDaysISO(day, windowDays);
  const inWindow = (dep, b) => dayInWindow(dep.date, b);
  const gap = (day, b) => Math.abs(daysBetweenISO(day, b.date));
  // Deposit days in a bank line's window, closest to its date first.
  const daysFor = (b) =>
    [...new Set(deposits.map((d) => d.date))]
      .filter((d) => dayInWindow(d, b))
      .sort((x, y) => gap(x, b) - gap(y, b));
  const take = (rule, confidence, ds, bs) => {
    ds.forEach((d) => usedDep.add(d.id));
    bs.forEach((b) => usedBank.add(b.id));
    const early = bs.some((b) => ds.some((d) => b.date < d.date));
    suggestions.push({
      rule,
      confidence: early ? "check" : confidence,
      deposits: ds,
      banks: bs,
      early,
    });
  };

  // 1:1 by amount, preferring the nearest bank date, for one verdict.
  const oneToOne = (wanted, rule, confidence) => {
    for (const dep of deposits) {
      if (usedDep.has(dep.id)) continue;
      const [hit] = freeBank()
        .filter(
          (b) =>
            cents(b.amount) === cents(dep.amount) &&
            inWindow(dep, b) &&
            verdict(dep, b.category, b) === wanted,
        )
        .sort((x, y) => gap(dep.date, x) - gap(dep.date, y));
      if (hit) take(rule, confidence, [dep], [hit]);
    }
  };

  const runRules = () => {
    // exact: amount + memo agree.
    oneToOne(AGREE, "exact", "exact");

    // moneris: one batch line = a subset of one day's card deposits. Run exact
    // first; with `tolerance` it runs again under `near` below.
    const moneris = (tolerance) => {
      for (const b of freeBank().filter((x) => x.category === MONERIS && cents(x.amount) > 0)) {
        for (const day of daysFor(b)) {
          const cards = deposits.filter(
            (d) => !usedDep.has(d.id) && d.date === day && CARD_FIELDS.has(depositField(d)),
          );
          const subset = closestSubset(cards, cents(b.amount), tolerance);
          if (subset) {
            take("moneris", tolerance ? "check" : "exact", subset, [b]);
            break;
          }
        }
      }
    };
    moneris(0);

    // sum: one deposit = several bank lines of its own type, on any days in the
    // window. Insurance gets a larger cap: one payment per insurer.
    for (const dep of deposits) {
      if (usedDep.has(dep.id)) continue;
      const category = CATEGORY_FOR_FIELD[depositField(dep)];
      if (!category) continue;
      const pool = freeBank().filter(
        (b) => b.category === category && inWindow(dep, b) && verdict(dep, b.category, b) === AGREE,
      );
      if (pool.length < 2) continue;
      const maxItems = category === INSURANCE ? 8 : 6;
      const subset = subsetSum(pool, cents(dep.amount), { maxItems });
      if (subset && subset.length > 1) {
        // Many small lines over many days can add up to almost any total by
        // chance, so a long sum is offered for checking, not as exact.
        const days = new Set(subset.map((b) => b.date));
        const span = daysBetweenISO(
          dep.date,
          subset.reduce((m, b) => (b.date > m ? b.date : m), dep.date),
        );
        const sure = subset.length <= SURE_SUM_LINES && span <= SURE_SUM_DAYS && days.size <= 2;
        take("sum", sure ? "exact" : "check", [dep], subset);
      }
    }

    // cheque: one cheque line = that day's Checks + Insurance cheque.
    for (const b of freeBank().filter((x) => x.category === CHEQUE)) {
      for (const day of daysFor(b)) {
        const pair = deposits.filter(
          (d) =>
            !usedDep.has(d.id) &&
            d.date === day &&
            (depositField(d) === "collection_checks" ||
              depositField(d) === "collection_insurance_check"),
        );
        if (
          pair.length === 2 &&
          cents(pair[0].amount) + cents(pair[1].amount) === cents(b.amount)
        ) {
          take("cheque", "exact", pair, [b]);
          break;
        }
      }
    }

    // amount: equal to the cent, memo has no opinion.
    oneToOne(UNKNOWN, "amount", "check");

    // near Moneris: a batch within NEAR_CENTS of a day's card deposits. Before
    // 1:1 near, so one card line can't take a batch the whole day adds up to.
    moneris(NEAR_CENTS);

    // near: same kind, amounts within NEAR_CENTS. Closest amount first, then
    // the nearest bank date.
    for (const dep of deposits) {
      if (usedDep.has(dep.id)) continue;
      const off = (b) => Math.abs(cents(b.amount) - cents(dep.amount));
      const [hit] = freeBank()
        .filter(
          (b) =>
            cents(b.amount) > 0 &&
            off(b) > 0 &&
            off(b) <= NEAR_CENTS &&
            inWindow(dep, b) &&
            verdict(dep, b.category, b) === AGREE,
        )
        .sort((a, b) => off(a) - off(b) || gap(dep.date, a) - gap(dep.date, b));
      if (hit) take("near", "check", [dep], [hit]);
    }
  };

  runRules();
  // Second pass on what is left: bank lines up to `backDays` before.
  back = backDays;
  runRules();

  return {
    suggestions,
    deposits: deposits.filter((d) => !usedDep.has(d.id)),
    bank: bank.filter((b) => !usedBank.has(b.id)),
    excluded,
  };
}

/** Short human label for a suggestion, e.g. "Insurance · 4 payers". */
export function suggestionLabel(sug) {
  const label = ruleLabel(sug);
  return sug.early ? `${label} · bank dated earlier` : label;
}

function ruleLabel({ rule, deposits, banks, early }) {
  switch (rule) {
    case "exact":
      return `${CATEGORY_LABELS[banks[0]?.category] ?? "Same type"} · ${early ? "same amount" : "exact"}`;
    case "moneris":
      return `Moneris · ${deposits.length} card types`;
    case "sum": {
      const type = CATEGORY_LABELS[banks[0]?.category] ?? "Same type";
      const days = new Set(banks.map((b) => b.date)).size;
      return `${type} · ${banks.length} lines${days > 1 ? ` over ${days} days` : ""}`;
    }
    case "cheque":
      return "Cheque · Checks + Ins. cheque";
    case "amount":
      return "Same amount · check type";
    case "near":
      return `${CATEGORY_LABELS[banks[0]?.category] ?? "Same type"} · close amount`;
    default:
      return "Suggested";
  }
}
